import { useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Button, Toast, type ToastType } from "@legal-500/legal500-design-system";
import EmptyState from "../../../components/EmptyState/index.js";
import LoadingState from "../../../components/LoadingState/index.js";
import { UnauthorizedView } from "../../../components/UnauthorizedView/index.js";
import { usePermissions } from "../../../hooks/usePermissions.js";
import {
  commentaries,
  type Commentary,
  type UpdateCommentaryRequest,
} from "../../../api/commentaries.service.js";
import { useMutation, useQuery } from "../../../hooks/useApi.js";
import { ROUTES } from "../../../routes.js";
import CommentaryForm from "./CommentaryForm.js";
import {
  buildUpdateCommentaryRequest,
  mapCommentaryToFormInitialState,
  type CommentaryFormValues,
  type TaxonomyLabelMaps,
  type TaxonomySelectionState,
} from "./commentaryForm.helpers.js";
import styles from "./CreateCommentary.module.css";

type EditCommentaryLocationState = {
  returnSearch?: string;
  prefetchedCommentary?: Commentary;
};

function EditCommentaryPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const { hasPermission } = usePermissions();
  const canChangeStatus = hasPermission("staff.research.change_status");
  const canApprove = hasPermission("staff.research.approve");
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  const locationState = (location.state as EditCommentaryLocationState | null) ?? null;
  const prefetchedCommentary =
    locationState?.prefetchedCommentary && locationState.prefetchedCommentary.id === id
      ? locationState.prefetchedCommentary
      : null;
  const prefetchedRef = useRef(prefetchedCommentary);

  const returnPath = `${ROUTES.research.commentaries}${locationState?.returnSearch ?? ""}`;

  const {
    data: commentary,
    loading,
    error,
  } = useQuery(() => {
    if (!id) {
      throw new Error("Commentary id is missing");
    }

    if (prefetchedRef.current) {
      return Promise.resolve(prefetchedRef.current);
    }

    return commentaries.getById(id);
  }, [id]);

  const { mutate: updateCommentary } = useMutation<
    Awaited<ReturnType<typeof commentaries.update>>,
    [string, UpdateCommentaryRequest]
  >(commentaries.update);

  const { mutate: submitForReview, loading: isSubmitting } = useMutation<
    Awaited<ReturnType<typeof commentaries.submitForReview>>,
    [string]
  >(commentaries.submitForReview);

  const { mutate: approve, loading: isApproving } = useMutation<
    Awaited<ReturnType<typeof commentaries.approve>>,
    [string]
  >(commentaries.approve);

  const { mutate: returnToDraft, loading: isReturningToDraft } = useMutation<
    Awaited<ReturnType<typeof commentaries.returnToDraft>>,
    [string]
  >(commentaries.returnToDraft);

  const { mutate: markReadyForProduct, loading: isMarkingReadyForProduct } = useMutation<
    Awaited<ReturnType<typeof commentaries.markReadyForProduct>>,
    [string]
  >(commentaries.markReadyForProduct);

  const saveFormRef = useRef<(() => Promise<void>) | null>(null);

  // Memoize so the reference is stable across re-renders (e.g. a background
  // Frontegg token refresh re-rendering this page). A new reference each render
  // would re-fire CommentaryForm's reset() effect and wipe unsaved edits.
  const initialState = useMemo(
    () => (commentary ? mapCommentaryToFormInitialState(commentary) : null),
    [commentary],
  );

  const isUpdatingStatus =
    isSubmitting || isApproving || isReturningToDraft || isMarkingReadyForProduct;

  if (loading) {
    return <LoadingState message="Loading commentary..." />;
  }

  if (error) {
    const status = (error as Error & { status?: number }).status;
    if (status === 401 || status === 403) {
      return <UnauthorizedView />;
    }

    const isNotFound = status === 404;
    return (
      <div className={styles.wrapper}>
        <EmptyState
          variant="error"
          title={isNotFound ? "Commentary not found" : "Failed to load commentary"}
          message={error.message}
          action={{
            label: "Back to List",
            onClick: () => navigate(returnPath),
          }}
        />
      </div>
    );
  }

  if (!id || !commentary || !initialState) {
    return (
      <div className={styles.wrapper}>
        <EmptyState
          variant="error"
          title="Commentary not found"
          message="The requested commentary could not be found."
          action={{
            label: "Back to List",
            onClick: () => navigate(returnPath),
          }}
        />
      </div>
    );
  }

  const handleSubmit = async (
    values: CommentaryFormValues,
    _selectionState: TaxonomySelectionState,
    _labelMaps: TaxonomyLabelMaps,
  ) => {
    const request = buildUpdateCommentaryRequest(values);
    await updateCommentary(id, request);
  };

  const statusActionLabels: Record<string, string> = {
    submit: "Submitted for review",
    approve: "Commentary approved",
    returnToDraft: "Returned to draft",
    markReadyForProduct: "Marked ready for product",
  };

  const handleStatusChange = async (
    action: "submit" | "approve" | "returnToDraft" | "markReadyForProduct",
  ) => {
    try {
      if (action === "submit" && saveFormRef.current) {
        await saveFormRef.current();
      }
      switch (action) {
        case "submit":
          await submitForReview(id);
          break;
        case "approve":
          await approve(id);
          break;
        case "returnToDraft":
          await returnToDraft(id);
          break;
        case "markReadyForProduct":
          await markReadyForProduct(id);
          break;
      }
      navigate(returnPath, {
        state: { toast: { message: statusActionLabels[action], type: "success" } },
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to update status";
      setToast({ message: errorMessage, type: "error" });
    }
  };

  const renderStatusActions = () => {
    if (!commentary) return null;

    const actions: React.ReactNode[] = [];

    if (canChangeStatus && commentary.status === "DRAFT") {
      actions.push(
        <Button
          key="submit"
          type="button"
          onClick={() => handleStatusChange("submit")}
          disabled={isUpdatingStatus}
          variant="outlined"
          color="primary"
        >
          Submit for Review
        </Button>,
      );
    }

    if (canApprove && commentary.status === "TO_REVIEW") {
      actions.push(
        <Button
          key="approve"
          type="button"
          onClick={() => handleStatusChange("approve")}
          disabled={isUpdatingStatus}
          variant="contained"
          color="primary"
        >
          Approve
        </Button>,
      );
    }

    if (canApprove && commentary.status === "APPROVED") {
      actions.push(
        <Button
          key="ready"
          type="button"
          onClick={() => handleStatusChange("markReadyForProduct")}
          disabled={isUpdatingStatus}
          variant="contained"
          color="primary"
        >
          Mark Ready for Product
        </Button>,
      );
    }

    if (
      (canChangeStatus && commentary.status === "TO_REVIEW") ||
      (canApprove && commentary.status === "APPROVED") ||
      (canApprove && commentary.status === "READY_FOR_PRODUCT")
    ) {
      actions.push(
        <Button
          key="return"
          type="button"
          onClick={() => handleStatusChange("returnToDraft")}
          disabled={isUpdatingStatus}
          variant="outlined"
          color="default"
        >
          Return to Draft
        </Button>,
      );
    }

    return actions.length > 0 ? <>{actions}</> : null;
  };

  return (
    <>
      <CommentaryForm
        key={id}
        mode="edit"
        title="Edit Commentary"
        readOnly={
          commentary.status === "APPROVED" ||
          commentary.status === "TO_REVIEW" ||
          commentary.status === "READY_FOR_PRODUCT"
        }
        statusActions={renderStatusActions()}
        submitLabel="Update"
        loadingLabel="Updating..."
        initialValues={initialState.values}
        initialSelectionState={initialState.selectionState}
        saveRef={saveFormRef}
        onSubmit={handleSubmit}
        onCancel={() => navigate(returnPath)}
        onDone={() => navigate(returnPath)}
      />
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={5000}
        />
      )}
    </>
  );
}

export default EditCommentaryPage;
